import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { IoIosAddCircle } from "react-icons/io";
import { FiTrash2 } from "react-icons/fi";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import AddPayment from "../../component/AddPayment";
import AdminPagination from "../../component/pagination/AdminPagination";
import SkeletonLoading from "../../component/SkeletonLoading";
import ToggleSwitch from "../../component/ToggleSwitch";
import Tooltip from "../../component/Tooltip";
import i18n from "../../i8next";
import {
  useChangeStatusMutation,
  useGetPaymentPlanQuery,
  useDeletePaymentPlanMutation,
} from "../../store/api/admin/paymentSlice";
import { capitalizeFirstLetter } from "../../utils/dateAndTimeConversion";

const initialPagination = {
  limit: 10,
  page: 1,
};

const PlansPayments = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [editData, setEditData] = useState(null);
  const [id, setId] = useState("");

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetPaymentPlanQuery({
      page: pagination.page,
      limit: pagination.limit,
    });

  // change status Payment Plan
  const [changeStatus, { isLoading: isStatusLoading }] =
    useChangeStatusMutation();

  const [deletePlan, { isLoading: isDeleting }] =
    useDeletePaymentPlanMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setPlans(data?.data?.plans);
      setPagination((prev) => ({
        ...prev,
        ...data.data.pagination,
      }));
    }
  }, [data, isSuccess]);

  // Payment Plan active/unActive
  const handleStatusChange = async (id) => {
    setId(id);
    try {
      const response = await changeStatus(id).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message);
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleOpenModal = (item = null) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData(null);
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePlan(deleteTarget.id).unwrap();
      toast.success("Plan deleted successfully.");
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete plan.");
      setShowDeleteModal(false);
    }
  };

  // Change Language Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        <div className="total">
          {t("total")}: {pagination.total}
        </div>
        {/* <div className="mb-2 total">Total Plans: {pagination.total}</div> */}
        <div className="">
          <Button
            className="d-flex align-items-center gap-1 mb-2"
            onClick={() => handleOpenModal()}
          >
            <IoIosAddCircle size={15} fill="white" />
            {t("add")} {t("payment")}
          </Button>
        </div>
      </div>
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("SR")}.{t("no")}.
              </th>
              <th className="text-nowrap">
                {t("user")} {t("type")}
              </th>
              <th>{t("name")}</th>
              <th
                className="text-nowrap"
                style={{ minWidth: "400px", width: "auto" }}
              >
                {t("description")}
              </th>
              <th className="text-nowrap">
                {t("max")} {t("seats")} ({t("riders")})
              </th>
              <th className="text-nowrap">
                {t("max")} {t("vehicles")} {t("limit")}
              </th>
              <th className="text-nowrap">
                {t("price")} {t("cents")}
              </th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonLoading MainLength={10} length={8} />
            ) : plans?.length > 0 ? (
              plans?.map((item, i) => {
                return (
                  <tr key={i}>
                    <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td className="text-nowrap">
                      {capitalizeFirstLetter(item?.user_type)}
                    </td>
                    <td className="text-nowrap">
                      {capitalizeFirstLetter(item?.name)}
                    </td>
                    <td style={{ minWidth: "400px" }}>{item?.description}</td>
                    <td className="text-nowrap">{item?.parent_max_limit}</td>
                    <td className="text-nowrap">{item?.vehicle_max_limit}</td>
                    <td className="text-nowrap">{item?.price_cents}</td>
                    <td>
                      <div className="actionBtn d-flex gap-2 align-items-center">
                        <ToggleSwitch
                          checked={item.is_active}
                          disabled={isStatusLoading && id === item.id}
                          onChange={() => handleStatusChange(item.id)}
                          title={
                            item.is_active
                              ? t("click_to_deactivate")
                              : t("click_to_activate")
                          }
                        />

                        <Tooltip title={t("edit")}>
                          <Button
                            className="rounded-circle"
                            onClick={() => handleOpenModal(item)}
                          >
                            <FiEdit />
                          </Button>
                        </Tooltip>

                        <Tooltip title={t("delete")}>
                          <Button
                            className="rounded-circle btn-danger"
                            onClick={() => handleDeleteClick(item)}
                          >
                            <FiTrash2 />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="text-center">
                <td colSpan="12">
                  <div>
                    <span>
                      {t("no")} {t("details")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">
            {editData?.id
              ? `${t("edit")} ${t("subscription")} ${t("plan")}`
              : `${t("add")} ${t("payment")} ${t("plan")}`}
          </Modal.Title>
        </Modal.Header>
        <AddPayment
          handleClose={handleCloseModal}
          editPlan={editData}
          onSuccess={handleCloseModal}
        />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">Delete Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the plan{" "}
            <strong>"{deleteTarget?.name}"</strong> ({deleteTarget?.user_type})?
          </p>
          <p className="text-muted small mb-0">
            ⚠ If any school or vendor has an active subscription on this plan,
            deletion will be blocked. Deactivate the plan instead.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <span className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </span>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PlansPayments;
